import React from "react"
import clsx from "clsx"

import { ReactComponent as ArrowIcon } from "assets/icons/arrow.svg"

import { parseAndFormatHistoricalUSD, UNAVAILABLE_DASH } from "../../util/formatter"

interface PeriodStatsTableProps {
  data: {
    period: string
    wagered: string
    profit: string
    // SOFT-555: `null` in microservice mode — bonus-service has no per-period transferred-bonus
    // aggregate yet, so the fail-closed backend sends `null` instead of a stale shadow. Render "—".
    bonuses: string | null
    rewards: string
  }[]
}

const renderValueWithArrow = (value: string, textColored?: boolean) => {
  const num = parseFloat(value.replace(/[^0-9.-]+/g, ""))
  const isPos = !isNaN(num) && num > 0
  const isNeg = !isNaN(num) && num < 0

  const formatted = parseAndFormatHistoricalUSD(value)

  return (
    <span className="flex items-center gap-1">
      <span
        className={clsx({
          ["text-green"]: isPos && textColored,
          ["text-red"]: isNeg && textColored
        })}
      >
        {formatted || "-"}
      </span>
      {isPos && (
        <span className="text-green rotate-180">
          <ArrowIcon />
        </span>
      )}
      {isNeg && (
        <span className="text-red">
          <ArrowIcon />
        </span>
      )}
    </span>
  )
}

export const PeriodStatsTable: React.FC<PeriodStatsTableProps> = ({ data }) => {
  if (!data || data.length === 0) return null

  return (
    <div className="w-full rounded-lg border border-[rgba(0,0,0,0.25)] dark:border-[rgba(255,255,255,0.15)] overflow-x-auto">
      <table className="table table-compact w-full">
        <thead className="bg-[rgba(212,212,212,0.14)] dark:bg-[rgba(212,212,212,0.1)] text-left text-[rgba(0,0,0,0.7)] dark:text-[rgba(255,255,255,0.46)] text-sm">
          <tr>
            <th className="px-4 py-3 border-b">Period</th>
            <th className="px-4 py-3 border-b">Wagered</th>
            <th className="px-4 py-3 border-b">Profit</th>
            <th className="px-4 py-3 border-b">Bonuses</th>
            <th className="px-4 py-3 border-b">Rewards</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className="text-base odd:bg-[rgba(212,212,212,0.7)] odd:dark:bg-[rgba(255,255,255,0.04)]">
              <td className="px-4 py-4 capitalize text-black dark:text-white">{row.period}</td>
              <td className="px-4 py-4 text-black dark:text-white">{renderValueWithArrow(row.wagered)}</td>
              <td className="px-4 py-4 text-black dark:text-white">{renderValueWithArrow(row.profit || "0", true)}</td>
              <td className="px-4 py-4 text-black dark:text-white">
                {row.bonuses === null || row.bonuses === undefined ? UNAVAILABLE_DASH : renderValueWithArrow(row.bonuses)}
              </td>
              <td className="px-4 py-4 text-black dark:text-white">{renderValueWithArrow(row.rewards)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
